import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ClipboardText, CheckCircle, IdentificationBadge } from "@phosphor-icons/react";
import { api, apiDelete, apiError } from "@/lib/api";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import EmployeePicker from "@/components/EmployeePicker";

const emptyForm = () => ({ task_name: "", area: "", date: "", supervisor: "", steps: [], signatures: [], status: "draft", project_id: defaultProject() });
const emptySign = () => ({ worker_name: "", worker_id: "", position: "", employee_id: "" });

export default function JsaModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [signOpenId, setSignOpenId] = useState(null);
  const [signForm, setSignForm] = useState(emptySign());
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/hse/jsa").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!assertProject(form.project_id)) return;
    try {
      if (editing) await api.put(`/hse/jsa/${editing}`, form);
      else await api.post("/hse/jsa", form);
      toast.success("AST guardado");
      setOpen(false); setEditing(null); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = useConfirmDelete();
  const remove = async (id, title = "") => {
    const ok = await confirmDelete({
      title: "¿Eliminar AST?",
      itemLabel: title,
      description: "El AST se archivará en el historial de auditoría con tu firma y motivo.",
    });
    if (!ok) return;
    try { await apiDelete(`/hse/jsa/${id}`, ok.reason); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const addStep = () => setForm({ ...form, steps: [...form.steps, { step_no: form.steps.length + 1, description: "", hazards: [], controls: [] }] });

  const sign = async () => {
    try {
      await api.post(`/hse/jsa/${signOpenId}/sign`, { ...signForm, signed: true });
      toast.success("Firma registrada");
      setSignOpenId(null); setSignForm(emptySign()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pickSigner = (emp) => setSignForm({
    worker_name: emp.name || "",
    worker_id: emp.dni || "",
    position: emp.position || "",
    employee_id: emp.id,
  });
  const unlinkSigner = () => setSignForm({ ...signForm, employee_id: "" });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{items.length} AST registrados</p>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm()); } }}>
            <DialogTrigger asChild><Button data-testid="jsa-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={14} weight="bold" /> Nuevo AST</Button></DialogTrigger>
            <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} AST — Análisis de Trabajo Seguro</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <ProjectField className="col-span-2" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="jsa-project" />
                <div className="col-span-2"><Label className="overline-label">Tarea</Label>
                  <Input data-testid="jsa-task" value={form.task_name} onChange={(e) => setForm({ ...form, task_name: e.target.value })} /></div>
                <div><Label className="overline-label">Área</Label>
                  <Input data-testid="jsa-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
                <div><Label className="overline-label">Supervisor</Label>
                  <Input data-testid="jsa-supervisor" value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} /></div>
                <div><Label className="overline-label">Fecha</Label>
                  <DateField testid="jsa-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline-label">Pasos de la tarea</p>
                    <Button size="sm" variant="ghost" data-testid="jsa-add-step" onClick={addStep} className="h-7 text-xs text-blue-900"><Plus size={12} weight="bold" /> Paso</Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase text-[10px]">
                      <tr><th className="text-left w-8">#</th><th className="text-left">Descripción del paso</th><th className="text-left">Peligros</th><th className="text-left">Controles</th><th></th></tr>
                    </thead>
                    <tbody>
                      {form.steps.map((st, idx) => (
                        <tr key={idx}>
                          <td className="align-top pt-2">{idx + 1}</td>
                          <td className="pr-1"><Textarea rows={2} className="text-xs" value={st.description} onChange={(e) => { const n=[...form.steps]; n[idx]={...n[idx],description:e.target.value}; setForm({...form,steps:n}); }} data-testid={`jsa-step-desc-${idx}`} /></td>
                          <td className="pr-1"><Textarea rows={2} className="text-xs" placeholder="Un peligro por línea" value={(st.hazards || []).join("\n")} onChange={(e) => { const n=[...form.steps]; n[idx]={...n[idx],hazards:e.target.value.split("\n").filter(Boolean)}; setForm({...form,steps:n}); }} /></td>
                          <td className="pr-1"><Textarea rows={2} className="text-xs" placeholder="Un control por línea" value={(st.controls || []).join("\n")} onChange={(e) => { const n=[...form.steps]; n[idx]={...n[idx],controls:e.target.value.split("\n").filter(Boolean)}; setForm({...form,steps:n}); }} /></td>
                          <td className="align-top pt-2"><button onClick={() => setForm({ ...form, steps: form.steps.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <Button data-testid="jsa-save" onClick={save} disabled={!form.task_name || !form.area} className="bg-blue-900 hover:bg-blue-800 w-full">{editing ? "Actualizar" : "Crear"}</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-lg">Sin AST registrados</p>}
        {items.map((it) => (
          <div key={it.id} data-testid={`jsa-card-${it.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ClipboardText size={18} weight="duotone" className="text-blue-900" />
                <p className="font-heading font-bold text-slate-900">{it.task_name}</p>
                <span className="text-xs text-slate-500">{it.area} · {it.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-600">{(it.signatures || []).filter((s) => s.signed).length} firmas</span>
                <Button size="sm" variant="outline" data-testid={`jsa-sign-${it.id}`} onClick={() => setSignOpenId(it.id)} className="h-7 text-xs gap-1"><CheckCircle size={12} weight="bold" /> Firmar</Button>
                {canWrite && <button data-testid={`jsa-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>}
              </div>
            </div>
            {(it.steps || []).length > 0 && (
              <div className="p-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase text-[10px]">
                    <tr><th className="text-left py-1 w-8">#</th><th className="text-left">Paso</th><th className="text-left">Peligros</th><th className="text-left">Controles</th></tr>
                  </thead>
                  <tbody>
                    {it.steps.map((st, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="py-1.5">{idx + 1}</td>
                        <td className="py-1.5">{st.description}</td>
                        <td className="py-1.5"><ul>{(st.hazards || []).map((h, i) => <li key={i}>• {h}</li>)}</ul></td>
                        <td className="py-1.5"><ul>{(st.controls || []).map((c, i) => <li key={i} className="text-emerald-700">✓ {c}</li>)}</ul></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {signOpenId && (
        <Dialog open onOpenChange={(o) => { if (!o) { setSignOpenId(null); setSignForm(emptySign()); } }}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle>Firmar AST</DialogTitle></DialogHeader>
            {signForm.employee_id ? (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-2" data-testid="jsa-sign-linked">
                <IdentificationBadge size={18} weight="fill" className="text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0 text-xs">
                  <p className="font-semibold text-slate-900 truncate">{signForm.worker_name}</p>
                  <p className="text-[10px] text-slate-500 truncate">DNI {signForm.worker_id || "-"} · {signForm.position || "-"} · <span className="text-emerald-700 font-bold">Nómina</span></p>
                </div>
                <button onClick={unlinkSigner} className="text-[10px] text-slate-500 hover:text-red-600 font-semibold" data-testid="jsa-sign-unlink">Editar</button>
              </div>
            ) : (
              <>
                <Label className="overline-label">Buscar en nómina (opcional)</Label>
                <EmployeePicker onPick={pickSigner} testid="jsa-sign-picker" placeholder="Buscar por nombre/DNI…" />
                <p className="text-[10px] text-slate-500 mt-1 mb-2 italic">Visitantes o externos pueden escribirse manualmente abajo.</p>
              </>
            )}
            <Label className="overline-label">Nombre completo</Label>
            <Input data-testid="jsa-sign-name" value={signForm.worker_name} onChange={(e) => setSignForm({ ...signForm, worker_name: e.target.value })} />
            <Label className="overline-label">DNI / N° empleado</Label>
            <Input data-testid="jsa-sign-id" value={signForm.worker_id} onChange={(e) => setSignForm({ ...signForm, worker_id: e.target.value })} />
            <Label className="overline-label">Cargo</Label>
            <Input data-testid="jsa-sign-position" value={signForm.position} onChange={(e) => setSignForm({ ...signForm, position: e.target.value })} />
            <p className="text-[11px] text-slate-500">Al firmar declaro que entendí los peligros y controles.</p>
            <Button data-testid="jsa-sign-confirm" onClick={sign} disabled={!signForm.worker_name} className="bg-emerald-600 hover:bg-emerald-700">Firmar</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
